import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { DashboardCategories, IHouseAccountBalance } from 'constants/balance.constants';
import { initialMessage, userMessage } from 'constants/ui.constants';
import { getDigitalBalanceRequests } from 'services/balances.service';
import type {
	IBeneficiaryDashboardRow,
	IBeneficiaryResponseTimeSummary,
	IBeneficiaryStatusSummary,
} from 'types/beneficiary-dashboard.types';

type IDashboardProps = { [key in DashboardCategories]?: null | [] };

interface IBalanceState extends IDashboardProps {
	houseBalances: IHouseAccountBalance[] | null;
	error: userMessage;
	isLoading: boolean;
	dashboardDisplayedCurrencies: [] | null;
	accountBalance: [] | null;
	pendingBalance: [] | null;
	digitalBalanceRequests?: {
		[key: string]: {
			data: any[];
			pagination: { total: number; hasMore: boolean };
		};
	};
	conversionFees: [] | null;
	paymentFees: [] | null;
	monthlySubscriptions: [] | null;
	planFees: [] | null;
	averageIncomeFromCustomer: number;
	beneficiaryStatusSummary: IBeneficiaryStatusSummary | null;
	beneficiaryResponseTimeSummary: IBeneficiaryResponseTimeSummary | null;
	beneficiaryDashboardRows: IBeneficiaryDashboardRow[];
	beneficiaryDashboardTotal: number;
	beneficiaryCubeRequestId: number;
	beneficiaryDashboardListRequestId: number;
	beneficiaryStatusSummaryLoading: boolean;
	beneficiaryResponseTimeSummaryLoading: boolean;
	beneficiaryDashboardListLoading: boolean;
}

const initialState: IBalanceState = {
	houseBalances: null,
	error: initialMessage,
	isLoading: false,
	dashboardDisplayedCurrencies: null,
	accountBalance: null,
	pendingBalance: null,
	digitalBalanceRequests: {},
	conversionFees: null,
	paymentFees: null,
	monthlySubscriptions: null,
	planFees: null,
	averageIncomeFromCustomer: 0,
	beneficiaryStatusSummary: null,
	beneficiaryResponseTimeSummary: null,
	beneficiaryDashboardRows: [],
	beneficiaryDashboardTotal: 0,
	beneficiaryCubeRequestId: 0,
	beneficiaryDashboardListRequestId: 0,
	beneficiaryStatusSummaryLoading: true,
	beneficiaryResponseTimeSummaryLoading: true,
	beneficiaryDashboardListLoading: false,
};

export const balanceSlice = createSlice({
	name: 'balance',
	initialState,
	reducers: {
		setHouseBalances: (state, { payload }) => {
			payload.sort((a: IHouseAccountBalance, b: IHouseAccountBalance) => b.amount - a.amount);
			state.houseBalances = payload;
		},
		setAccountBalance: (state, { payload }) => {
			state.accountBalance = payload;
		},
		setPendingBalance: (state, { payload }) => {
			state.pendingBalance = payload;
		},
		setConversionFees: (state, { payload }) => {
			state.conversionFees = payload;
		},
		setPaymentFees: (state, { payload }) => {
			state.paymentFees = payload;
		},
		setPlanFees: (state, { payload }) => {
			state.monthlySubscriptions = payload;
		},
		setAverageIncomeFromCustomer: (state, { payload }) => {
			state.averageIncomeFromCustomer = payload;
		},
		setDashboardDisplayedCurrencies: (state, { payload }) => {
			state.dashboardDisplayedCurrencies = payload;
		},
		setError: (state, action: PayloadAction<userMessage>) => {
			state.error = action.payload;
		},
		setLoading: (state, { payload }) => {
			state.isLoading = payload;
		},
		clearDigitalBalanceRequests: (state) => {
			state.digitalBalanceRequests = {};
		},
		setBeneficiaryStatusSummary: (state, { payload }: PayloadAction<IBeneficiaryStatusSummary | null>) => {
			state.beneficiaryStatusSummary = payload;
		},
		setBeneficiaryResponseTimeSummary: (
			state,
			{ payload }: PayloadAction<IBeneficiaryResponseTimeSummary | null>,
		) => {
			state.beneficiaryResponseTimeSummary = payload;
		},
		setBeneficiaryDashboardList: (
			state,
			{ payload }: PayloadAction<{ rows: IBeneficiaryDashboardRow[]; total: number }>,
		) => {
			state.beneficiaryDashboardRows = payload.rows;
			state.beneficiaryDashboardTotal = payload.total;
		},
		beginBeneficiaryCubeRequests: (state) => {
			state.beneficiaryCubeRequestId += 1;
			state.beneficiaryStatusSummaryLoading = true;
			state.beneficiaryResponseTimeSummaryLoading = true;
			state.error = initialMessage;
		},
		invalidateBeneficiaryCubeRequests: (state) => {
			state.beneficiaryCubeRequestId += 1;
			state.beneficiaryStatusSummaryLoading = false;
			state.beneficiaryResponseTimeSummaryLoading = false;
		},
		setBeneficiaryStatusSummaryLoading: (state, { payload }: PayloadAction<boolean>) => {
			state.beneficiaryStatusSummaryLoading = payload;
		},
		setBeneficiaryResponseTimeSummaryLoading: (state, { payload }: PayloadAction<boolean>) => {
			state.beneficiaryResponseTimeSummaryLoading = payload;
		},
		beginBeneficiaryDashboardListRequest: (state) => {
			state.beneficiaryDashboardListRequestId += 1;
			state.beneficiaryDashboardListLoading = true;
			state.error = initialMessage;
		},
		setBeneficiaryDashboardListLoading: (state, { payload }: PayloadAction<boolean>) => {
			state.beneficiaryDashboardListLoading = payload;
		},
		clearBeneficiaryDashboardList: (state) => {
			state.beneficiaryDashboardListRequestId += 1;
			state.beneficiaryDashboardRows = [];
			state.beneficiaryDashboardTotal = 0;
			state.beneficiaryDashboardListLoading = false;
			state.error = initialMessage;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getDigitalBalanceRequests.fulfilled, (state, { payload }) => {
			if (payload && payload.documentedOnHyper !== undefined && payload.data) {
				if (!state.digitalBalanceRequests) {
					state.digitalBalanceRequests = {};
				}
				const key = payload.documentedOnHyper ? 'documented' : 'notDocumented';
				state.digitalBalanceRequests[key] = {
					data: Array.isArray(payload.data.result) ? payload.data.result : [],
					pagination: payload.data.pagination || { total: 0, hasMore: false },
				};
			}
		});
	},
});

export const getHouseBalances = (state: any) => {
	return state.balance.houseBalances;
};
export const getAccountBalance = (state: any) => {
	return state.balance.accountBalance;
};
export const getPendingBalance = (state: any) => {
	return state.balance.pendingBalance;
};
export const getConversionFees = (state: any) => {
	return state.balance.conversionFees;
};
export const getPaymentFees = (state: any) => {
	return state.balance.paymentFees;
};
export const getPlanFees = (state: any) => {
	return state.balance.monthlySubscriptions;
};
export const getAverageIncomeFromCustomer = (state: any) => {
	return state.balance.averageIncomeFromCustomer;
};
export const getDigitalBalanceRequestsData = (state: any, documented: boolean) => {
	const key = documented ? 'documented' : 'notDocumented';
	return (
		state.balance.digitalBalanceRequests?.[key] || {
			data: [],
			pagination: { total: 0, hasMore: false },
		}
	);
};
export const getDashboardDisplayedCurrencies = (state: any) => {
	return state.balance.dashboardDisplayedCurrencies;
};
export const getError = (state: any) => {
	return state.balance.error;
};
export const getLoading = (state: any) => {
	return state.balance.isLoading;
};
export const getBeneficiaryStatusSummaryData = (state: any) => state.balance.beneficiaryStatusSummary;
export const getBeneficiaryResponseTimeSummaryData = (state: any) =>
	state.balance.beneficiaryResponseTimeSummary;
export const getBeneficiaryDashboardRows = (state: any) => state.balance.beneficiaryDashboardRows;
export const getBeneficiaryDashboardTotal = (state: any) => state.balance.beneficiaryDashboardTotal;
export const getBeneficiaryStatusSummaryLoading = (state: any) =>
	state.balance.beneficiaryStatusSummaryLoading;
export const getBeneficiaryResponseTimeSummaryLoading = (state: any) =>
	state.balance.beneficiaryResponseTimeSummaryLoading;
export const getBeneficiaryDashboardListLoading = (state: any) =>
	state.balance.beneficiaryDashboardListLoading;
export const {
	setHouseBalances,
	setDashboardDisplayedCurrencies,
	setAccountBalance,
	setPendingBalance,
	setConversionFees,
	setPaymentFees,
	setPlanFees,
	setAverageIncomeFromCustomer,
	setError,
	setLoading,
	clearDigitalBalanceRequests,
	setBeneficiaryStatusSummary,
	setBeneficiaryResponseTimeSummary,
	setBeneficiaryDashboardList,
	beginBeneficiaryCubeRequests,
	invalidateBeneficiaryCubeRequests,
	setBeneficiaryStatusSummaryLoading,
	setBeneficiaryResponseTimeSummaryLoading,
	beginBeneficiaryDashboardListRequest,
	setBeneficiaryDashboardListLoading,
	clearBeneficiaryDashboardList,
} = balanceSlice.actions;

export default balanceSlice.reducer;
